import React, { useEffect, useRef, useState } from 'react'
import './momentum-form.styles.scss';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import ManageContentInformation from '../blocks/information.component';
import ManageContentVisibility from '../blocks/visibility.component';
import ManageContentIconCropped from '../blocks/icon.component';
import ManageContentCategories from '../blocks/categories.component';
import ManageContentDates from '../blocks/dates.component';
import ManageContentRewards from '../blocks/rewards.component';
import ManageMomentumDirection from '../blocks/momentum-direction.component';
import MomentumRemoval from './momentum-removal.component';
import { useSelector } from 'react-redux';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { UseCloudFunction } from '@/utils/firebase.utils';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import { membershipRestrictions, testMemberRestrictions } from '@/utils/membershipRestrictions';
import UpgradeRequiredModal from '@/components/SHARED/upgrade-required-modal/upgrade-required-modal.component';
import { RandomString } from '@/utils/randomString';
import FullPageLoader from '@/components/loader/full-page-loader.component';
import ShowHideSettingsButton from '../blocks/show-hide-button.component';
import ElementDelete from '@/components/SHARED/element-delete/element-delete.component';
import useAnalytics from '@/utils/useAnalytics';
import { getOptimizedBlob } from '@/utils/getOptimizedBlob';
import { createPathFromTimestamp } from '@/utils/createPathFromTimestamp';

const MomentumForm = ({
  gameElements,
  momentumCardsFromDb,
  gameData
}) => {

  const membership = useSelector(state => state.userData.membership);
  const userProfile = useSelector(state => state.userData.userProfile);
  const screenSize = useSelector(state => state.contentData.screenSize);
  const [formData, setFormData] = useState({})
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [existingDescription, setExistingDescription] = useState(null);
  const [clearEditor, setClearEditor] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { trackEvent, trackException } = useAnalytics();
  const { momentumCardPath } = useParams();
  const dataRef = useRef();
  const approveRef = useRef();
  const prevDirectionRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const storage = getStorage();

  useEffect(() => {
    if (!gameData || !membership || !location) return;
    if (location.pathname.includes('/elements/momentum/edit')) return;
    const res = testMemberRestrictions({ 'gameData': gameData, 'membership': membership, 'create': true });
    if (res.disabled && res.disabledReason.includes('elements')) {
      const planRest = membershipRestrictions[membership.membership];
      const message = `While using the ${planRest.title}, you are only able to have ${planRest.elementCount} active Game Elements per game.`;
      setShowUpgradeModal(message);
      return;
    }
  }, [gameData, membership, location])

  useEffect(() => {
    if (!momentumCardPath || !momentumCardsFromDb) {
      dataRef.current = {
        'opt_visible': true,
        'opt_available': true,
        'icon': '/images/icons/momentum2.png'
      };
      setFormData(dataRef.current);
      return;
    }
    dataRef.current = Object.values(momentumCardsFromDb).filter(e => e.path === momentumCardPath)[0];
    if (dataRef.current.opt_momentumDirection) {
      prevDirectionRef.current = dataRef.current.opt_momentumDirection;
    }
    setFormData(dataRef.current);
    if (dataRef.current.desc) {
      setExistingDescription(dataRef.current.desc);
    }
  }, [momentumCardPath, momentumCardsFromDb])

  useEffect(() => {
    if (!momentumCardsFromDb) { return; }
    let arr = [];
    for (let c of Object.values(momentumCardsFromDb)) {
      if (c.opt_cat && !arr.includes(c.opt_cat)) {
        arr.push(c.opt_cat)
      }
    }
    setCategories(arr.sort())
  }, [momentumCardsFromDb])

  useEffect(() => {
    const direction = formData.opt_momentumDirection;
    if (!direction || direction === prevDirectionRef.current) return;
    prevDirectionRef.current = direction;
    if (direction !== 'negative') {
      dataRef.current = {
        ...dataRef.current,
        opt_removeCurrency: null,
        opt_removeItems: null,
      };
      setFormData({ ...dataRef.current });
    }
    if (direction !== 'positive') {
      dataRef.current = {
        ...dataRef.current,
        opt_earnedPoints: null,
        opt_earnedCurrency: null,
        opt_earnedBadges: null,
        opt_earnedItems: null,
        opt_earnedPrizePacks: null,
      };
      setFormData({ ...dataRef.current });
    }
  }, [formData.opt_momentumDirection])

  function saveData(key, value) {
    dataRef.current = { ...dataRef.current, ...{ [key]: value } };
    setFormData(dataRef.current);
  }

  function cancelCreate() {
    setConfirmMessage('Are you sure you want to cancel? All of your data will be lost.');
    approveRef.current = () => {
      dataRef.current = {};
      setFormData(dataRef.current);
      navigate(-1)
    }
  }

  async function handleSubmit() {
    if (location.pathname.includes('/preview/manager/')) {
      setAlertMessage('Sorry! Data cannot be saved in Preview Mode.');
      return;
    }
    let tempData = { ...dataRef.current }
    if (!tempData.name) {
      setAlertMessage('Please include a name for this Momentum Card before saving.');
      return;
    }
    if (!tempData.icon) {
      setAlertMessage('Please include an icon for this Momentum Card before saving.');
      return;
    }
    if (!tempData.opt_momentumDirection) {
      setAlertMessage('Please choose a Momentum Direction before saving.');
      return;
    }
    if (
      gameElements &&
      Object.values(gameElements).filter(e =>
        e.name.trim().toLowerCase() === tempData.name.trim().toLowerCase() &&
        e.id !== tempData.id
      ).length > 0
    ) {
      setAlertMessage('There is already an element with this name. Please choose a different name for this Momentum Card.');
      setLoading(false);
      return;
    }
    setConfirmMessage('Are you sure you are ready to save this Momentum Card? All of these settings can be changed later.')
    approveRef.current = () => {
      finishApproval()
    }
  }

  async function finishApproval() {
    setLoading(true);
    let tempData = { ...dataRef.current }
    if (tempData.icon.upload) {
      const optimizedBlob = await getOptimizedBlob(tempData.icon.croppie, 20);
      console.log('Cropped image size in KB:', optimizedBlob.size / 1024);
      uploadImage(tempData.icon, optimizedBlob);
    } else if (tempData.icon.url) {
      saveMomentumCard(tempData.icon.url)
      return;
    } else if (tempData.icon) {
      saveMomentumCard(tempData.icon)
      return;
    } else {
      setAlertMessage('Something went wrong with the icon. Please try using a different image.');
      setLoading(false);
      return;
    }
  }

  function uploadImage(icon, blob) {
    const now = new Date().getTime();
    const path = `${userProfile.appId}/${now}-${icon.name}`;
    const iconRef = ref(storage, path);
    const metadata = {
      contentType: icon.type,
      customMetadata: {
        appId: userProfile.appId,
        email: userProfile.email
      }
    };
    uploadBytes(iconRef, blob, metadata).then((snapshot) => {
      getDownloadURL(iconRef)
        .then(async (url) => {
          saveMomentumCard(url);
          try {
            await UseCloudFunction(
              'saveToMyContent',
              {
                'gameId': gameData.gameId,
                'appId': userProfile.appId,
                'source': 'Momentum Icons',
                'category': 'Images',
                'cropped': true,
                'contentData': {
                  'name': icon.name,
                  'url': url,
                  'type': 'image',
                  'id': `${now}_${RandomString(10)}_image`
                },
              }
            )
          } catch (error) {
            console.error("Error saving to my content:", error);
            setAlertMessage('Something went wrong while saving the icon to My Content. Error: ' + error.message);
            trackException('Momentum Icon Save to My Content Error: ' + error.message);
          }
          trackEvent(
            'image_saved_to_my_content',
            'Game Manager',
            `Momentum icon ${icon.name} was uploaded for user ${userProfile.appId}.`,
            null,
            membership
          )
          return;
        })
    })
  }

  async function saveMomentumCard(iconUrl) {
    let tempData = { ...dataRef.current }

    const now = new Date().getTime();
    const gameCode = gameData.gameId.substring(gameData.gameId.indexOf('-') - 5, gameData.gameId.indexOf('-') + 6)
    let action = 'Edited'
    if (!tempData.id) {
      tempData.appId = userProfile.appId;
      tempData.gameId = gameData.gameId;
      tempData.id = now + '-' + gameCode + '-momentum';
      tempData.status = 'active';
      tempData.type = 'momentum';
      tempData.public = true;
      tempData.path = createPathFromTimestamp();
      tempData.opt_available = true;
      action = 'Created'
    }
    tempData.icon = iconUrl;
    const res = await UseCloudFunction(
      'saveGameElement',
      {
        'gameId': gameData.gameId,
        'appId': userProfile.appId,
        'elementData': tempData,
        'userName': `${userProfile.fName} ${userProfile.lName}`,
        'action': action
      }
    )
    if (res.error) {
      setAlertMessage('Something went wrong. Error: ' + res.error);
      setLoading(false);
      return;
    }
    setAlertMessage('Momentum Card saved successfully.');
    dataRef.current = {};
    setFormData(dataRef.current);
    setLoading(false);
    navigate(-1);
  }

  return (
    <div className={`create-momentum-form ${screenSize}`}>
      <span className='required meta'>* = Required</span>
      <div className="g-space-0-5"></div>
      <ManageContentInformation
        type='momentum'
        saveData={saveData}
        formData={formData}
        existingDescription={existingDescription}
        clearEditor={clearEditor}
        setClearEditor={setClearEditor}
        textEditorLabel='Momentum Card Description (optional)'
      />
      <div className='g-space-2'></div>
      <ManageContentIconCropped
        type='momentum'
        saveData={saveData}
        formData={formData}
      />
      <div className='g-space-2'></div>
      <ManageMomentumDirection
        formData={formData}
        saveData={saveData}
      />
      {
        formData.opt_momentumDirection === 'negative' &&
        <>
          <div className='g-space-2'></div>
          <MomentumRemoval
            formData={formData}
            saveData={saveData}
            gameElements={gameElements}
            gameData={gameData}
          />
        </>
      }
      {
        formData.opt_momentumDirection === 'positive' &&
        <>
          <div className='g-space-2'></div>
          <ManageContentRewards
            type='momentum'
            saveData={saveData}
            formData={formData}
            gameData={gameData}
            gameElements={gameElements}
            text='receiving this Momentum Card' />
        </>
      }
      <div className='g-space-1'></div>
      <ShowHideSettingsButton
        showAdvancedOptions={showAdvancedOptions}
        setShowAdvancedOptions={setShowAdvancedOptions}
      />
      <div className='g-space-1'></div>
      <div className={`aditional-options ${showAdvancedOptions ? 'show' : 'hide'}`}>
        <ManageContentCategories
          type='momentum'
          saveData={saveData}
          formData={formData}
          categories={categories}
        />
        <div className='g-space-2'></div>
        <ManageContentVisibility
          type='momentum'
          saveData={saveData}
          formData={formData}
          visibilityText="Turn visibility ON for this Momentum Card. If toggled off, it will be totally hidden from players regardless of whether or not they have received it."
          availabilityText="This Momentum Card can be earned through an achievement or a manager-distributed reward. If toggled off, the card cannot be earned in any way."
        />
        <div className='g-space-2'></div>
        <ManageContentDates
          type='momentum'
          saveData={saveData}
          formData={formData}
          text='This Momentum Card will only be available to be earned or distributed within these dates.'
        />
        <div className='g-space-1'></div>
        <ShowHideSettingsButton
          showAdvancedOptions={showAdvancedOptions}
          setShowAdvancedOptions={setShowAdvancedOptions}
        />
      </div>
      <div className="g-space-1"></div>
      <div className='buttons'>
        {
          (loading)
            ?
            <>
              {
                (formData.ts_created) &&
                <button
                  type='button'
                  className={`g-button small-btn red ${screenSize !== 'mobile' ? 'left-item' : ''}`}>
                  Delete
                </button>
              }
              <button
                type='button'
                className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}>
                Cancel
              </button>
              <button
                type='button'
                className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}>
                <ThreeDotsLoader />
              </button>
            </>
            :
            <>
              {
                (formData.ts_created) &&
                <ElementDelete
                  element={formData}
                  style='button'
                  classText={screenSize !== 'mobile' ? 'left-item' : ''} />
              }
              <button
                type='button'
                className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}
                onClick={() => cancelCreate()}>
                Cancel
              </button>
              <button
                type='button'
                className={`g-button submit-button`}
                onClick={() => handleSubmit()}>
                Save Momentum Card
              </button>
            </>
        }
      </div>
      <ModalAlert show={alertMessage} cancel={() => setAlertMessage(null)} message={alertMessage} />
      <ModalConfirm show={confirmMessage} cancel={() => setConfirmMessage(null)} message={confirmMessage} onApprove={approveRef.current} />
      <UpgradeRequiredModal
        show={showUpgradeModal}
        cancel={() => setShowUpgradeModal(false)}
        message={showUpgradeModal}
      />
      <FullPageLoader show={loading} />
    </div>
  )
}

export default MomentumForm
